import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Alert, Box, Grid, IconButton, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import { useConfirm } from 'components/common/GlobalConfirmDialog';
import PhoneControl from 'components/formik/PhoneControl';
import PhotoUpload from 'components/formik/PhotoUpload';
import TextField from 'components/formik/TextField';
import { connect, Field } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { bool, func, object, string } from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import {
  useDownloadOmmnioSignSignedZipMutation,
  useReleasePhoneMutation,
} from 'store/app/entities/EmployeesApiSlice';
import { I18n, Translate } from 'utils/i18n';
import {
  parseFilenameFromContentDisposition,
  saveBlobDownload,
} from 'utils/ommniosignSignedZipDownload';

const fieldSx = {
  m: 0,
  width: '100%',
  boxSizing: 'border-box',
  '& textarea': {
    p: 0,
  },
};

const inputsSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  mb: 1,
  '&:last-child': {
    mb: 0,
  },
};

const phoneWithActionSx = {
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 0,
  gap: 0.5,
};

const phoneFieldWrapSx = {
  flex: '1 1 auto',
  minWidth: 0,
};

const emailFieldWrapSx = {
  flex: '1 1 0',
  minWidth: 0,
};

const releasePhoneConfirmTitleBold = chunks => <strong>{chunks}</strong>;

const telChange = formik => async value => {
  if (isEmpty(value) || value.length < 9) {
    if (!formik.values.inviteDisabled) {
      formik.setFieldValue('inviteDisabled', true);
      formik.setFieldValue('invite', []);
    }
  } else if (formik.values.inviteDisabled) {
    formik.setFieldValue('inviteDisabled', false);
    formik.setFieldValue('invite', []);
  }
};

const PersonalDetailsControls = ({
  formik,
  country = 'es',
  isCompanyAdmin = false,
  employee = null,
  modifyEmployee = () => {},
  authCompany = null,
}) => {
  const { open: openConfirm, close: closeConfirm } = useConfirm();
  const [releasePhone, { isLoading: releasingPhone }] = useReleasePhoneMutation();
  const [downloadSignedZip] = useDownloadOmmnioSignSignedZipMutation();
  const signedZipDownloadLock = useRef(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const hasLinkedLogin = !!(
    (employee?.user && (employee.user._id || employee.user)) ||
    employee?.user_id ||
    formik.values.userId
  );
  const isAppUser = !!employee?.user?.isApp;
  const showReleaseLogin = isCompanyAdmin && employee?._id && hasLinkedLogin && !isAppUser;
  const companyUsesOmmnioSign =
    authCompany?.eSignatureProvider?.isActive === true &&
    authCompany?.eSignatureProvider?.name === 'ommniosign';

  const runSignedZipDownload = useCallback(async () => {
    if (signedZipDownloadLock.current) {
      return false;
    }
    signedZipDownloadLock.current = true;
    try {
      const { blob, contentDisposition } = await downloadSignedZip({ _id: employee._id }).unwrap();
      const serverName = parseFilenameFromContentDisposition(contentDisposition);
      saveBlobDownload(blob, serverName || 'mandatory-reads.zip');
      setSnackbar({
        open: true,
        message: I18n.t('EmployeeDrawer.ReleasePhone.downloadSignedZipSuccess'),
        severity: 'success',
      });
      return true;
    } catch (e) {
      const status = e?.status ?? e?.originalStatus;
      const msg =
        status === 404 || e?.originalStatus === 404
          ? I18n.t('EmployeeDrawer.ReleasePhone.downloadSignedZipEmpty')
          : I18n.t('EmployeeDrawer.ReleasePhone.downloadSignedZipError');
      setSnackbar({ open: true, message: msg, severity: 'error' });
      return false;
    } finally {
      signedZipDownloadLock.current = false;
    }
  }, [downloadSignedZip, employee?._id]);

  const handleReleasePhone = () => {
    const name = `${employee?.firstName || ''} ${employee?.surName || ''}`.trim();
    const runDisconnect = async () => {
      closeConfirm();
      try {
        const res = await releasePhone({ _id: employee._id }).unwrap();
        modifyEmployee(employee._id, res.data.employee);
        formik.setFieldValue('userId', '');
        formik.setFieldValue('phone', res.data.employee.phone || '');
        setSnackbar({
          open: true,
          message: I18n.t('EmployeeDrawer.ReleasePhone.success'),
          severity: 'success',
        });
      } catch (e) {
        const status = e?.status ?? e?.originalStatus;
        const serverMsg = e?.data?.data?.error?.message || e?.data?.error?.message;
        const msg =
          status === 409
            ? I18n.t('EmployeeDrawer.ReleasePhone.errorOtherOrgs')
            : serverMsg || I18n.t('EmployeeDrawer.ReleasePhone.errorGeneric');
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    };
    openConfirm({
      message: (
        <Translate
          value="EmployeeDrawer.ReleasePhone.confirmTitle"
          tag="span"
          name={name}
          b={releasePhoneConfirmTitleBold}
        />
      ),
      children: (
        <Alert severity="warning">
          <Typography component="div" variant="body2" paragraph>
            {I18n.t('EmployeeDrawer.ReleasePhone.confirmIntro')}
          </Typography>
          <Stack
            component="ul"
            spacing={0.75}
            sx={{ m: 0, pl: 2.5, listStyleType: 'disc', typography: 'body2' }}
          >
            {['confirmBulletInvite', 'confirmBulletDocuments', 'confirmBulletPrivacy'].map(key => (
              <Typography component="li" key={key} variant="body2" sx={{ display: 'list-item' }}>
                {I18n.t(`EmployeeDrawer.ReleasePhone.${key}`, { name })}
              </Typography>
            ))}
          </Stack>
        </Alert>
      ),
      buttons: [
        {
          buttonKey: 'release-phone-cancel',
          text: I18n.t('EmployeeDrawer.CancelButton'),
          onClick: closeConfirm,
          type: 'button',
        },
        ...(companyUsesOmmnioSign
          ? [
              {
                buttonKey: 'release-phone-download',
                text: I18n.t('EmployeeDrawer.ReleasePhone.confirmDownloadAndDisconnect'),
                onClick: async () => {
                  const ok = await runSignedZipDownload();
                  if (ok) {
                    closeConfirm();
                  }
                },
                type: 'button',
              },
            ]
          : []),
        {
          buttonKey: 'release-phone-disconnect',
          text: I18n.t('EmployeeDrawer.ReleasePhone.confirmDisconnect'),
          onClick: runDisconnect,
          variant: 'contained',
          color: 'primary',
          type: 'submit',
        },
      ],
    });
  };

  return (
    <Box sx={{ pt: '20px' }}>
      <Grid container>
        <Grid size={12}>
          <Typography variant="caption" gutterBottom>
            {I18n.t('EmployeeDrawer.PERSONAL DETAILS')}
          </Typography>
        </Grid>
        <Grid sx={{ display: 'flex' }} size={2}>
          <Field component={PhotoUpload} circle name="avatar" />
        </Grid>
        <Grid size={10}>
          <Box sx={inputsSx}>
            <Field
              component={TextField}
              sx={fieldSx}
              placeholder={`${I18n.t('EmployeeDrawer.First name')}${
                formik.values.allowAnonymousEmployees ? '' : '*'
              }`}
              name="firstName"
            />
            <Field
              component={TextField}
              sx={fieldSx}
              placeholder={`${I18n.t('EmployeeDrawer.Last name')}${
                formik.values.allowAnonymousEmployees ? '' : '*'
              }`}
              name="lastName"
            />
          </Box>
          <Box sx={inputsSx}>
            <Box sx={phoneWithActionSx}>
              <Box sx={phoneFieldWrapSx}>
                <Field
                  country={country}
                  component={PhoneControl}
                  specialLabel={false}
                  inputProps={{ type: 'tel' }}
                  bindChange={telChange(formik)}
                  placeholder={I18n.t('EmployeeDrawer.Phone number')}
                  name="phone"
                />
              </Box>
              {showReleaseLogin ? (
                <Tooltip title={I18n.t('EmployeeDrawer.ReleasePhone.tooltip')}>
                  <span>
                    <IconButton
                      aria-label={I18n.t('EmployeeDrawer.ReleasePhone.tooltip')}
                      size="small"
                      disabled={releasingPhone}
                      onClick={handleReleasePhone}
                      sx={fieldSx}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>
            <Box sx={emailFieldWrapSx}>
              <Field component={TextField} sx={fieldSx} placeholder="Email" name="email" />
            </Box>
          </Box>
          <Box sx={inputsSx}>
            <Field
              multiline
              component={TextField}
              sx={fieldSx}
              placeholder={I18n.t('EmployeeDrawer.Notes')}
              name="notes"
            />
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
PersonalDetailsControls.propTypes = {
  country: string,
  formik: object.isRequired,
  isCompanyAdmin: bool,
  employee: object,
  modifyEmployee: func,
  authCompany: object,
};

export default connect(PersonalDetailsControls);
